import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { FiArrowLeft, FiCheck } from 'react-icons/fi';
import {
  Container,
  BiometricContainer,
  Form,
  InputGroup,
  Label,
  Input,
  Button
} from '../styles/LoginStyles';

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #00A1FF;
  font-size: 0.95rem;
  cursor: pointer;
  margin-bottom: 2rem;
  padding: 0;
  
  &:hover {
    color: #0088cc;
  }
`;

const Title = styled.h2`
  font-size: 1.75rem;
  color: #1f2937;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const Subtitle = styled.p`
  color: #6b7280;
  margin-bottom: 2rem;
  font-size: 0.95rem;
`;

const OTPContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin-bottom: 2rem;
`;

const OTPInput = styled.input`
  width: 50px;
  height: 50px;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #00A1FF;
    box-shadow: 0 0 0 3px rgba(0, 161, 255, 0.1);
  }
`;

const ResendLink = styled.button`
  background: none;
  border: none;
  color: #00A1FF;
  cursor: pointer;
  font-size: 0.9rem;
  margin-top: 1rem;
  
  &:hover {
    text-decoration: underline;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PasswordStrengthBar = styled.div`
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  margin-top: 0.5rem;
  overflow: hidden;
`;

const PasswordStrengthFill = styled.div`
  height: 100%;
  width: ${props => props.strength}%;
  background: ${props => {
    if (props.strength < 40) return '#ef4444';
    if (props.strength < 70) return '#f59e0b';
    return '#10b981';
  }};
  transition: all 0.3s;
`;

const PasswordRequirements = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  font-size: 0.85rem;
`;

const Requirement = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  color: ${props => props.met ? '#10b981' : '#6b7280'};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const CheckIcon = styled(FiCheck)`
  color: #10b981;
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 2rem;
  
  svg {
    width: 64px;
    height: 64px;
    color: #10b981;
    margin-bottom: 1rem;
  }
  
  h2 {
    color: #1f2937;
    margin-bottom: 0.5rem;
  }
  
  p {
    color: #6b7280;
    margin-bottom: 2rem;
  }
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #dc2626;
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: Reset Password, 4: Success
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  // Password validation
  const passwordRequirements = {
    minLength: newPassword.length >= 8,
    hasUpperCase: /[A-Z]/.test(newPassword),
    hasNumber: /[0-9]/.test(newPassword),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
  };

  const allRequirementsMet = Object.values(passwordRequirements).every(Boolean);
  
  const passwordStrength = () => {
    let strength = 0;
    if (passwordRequirements.minLength) strength += 25;
    if (passwordRequirements.hasUpperCase) strength += 25;
    if (passwordRequirements.hasNumber) strength += 25;
    if (passwordRequirements.hasSpecialChar) strength += 25;
    return strength;
  };

  // Mock API call for sending OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    // Mock API call
    setTimeout(() => {
      setLoading(false);
      setStep(2);
      setResendTimer(60);
      
      // Start countdown
      const interval = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, 1000);
  };

  // Handle OTP input
  const handleOTPChange = (index, value) => {
    if (value.length > 1) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  // Handle OTP verification
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    
    const otpValue = otp.join('');
    if (otpValue.length !== 6) {
      setError('Please enter complete OTP');
      return;
    }
    
    setLoading(true);
    
    // Mock API call
    setTimeout(() => {
      setLoading(false);
      // Mock validation - accept any 6 digit OTP for demo
      if (otpValue.length === 6) {
        setStep(3);
      } else {
        setError('Invalid OTP. Please try again.');
      }
    }, 1000);
  };

  // Handle password reset
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!allRequirementsMet) {
      setError('Password does not meet all requirements');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    // Mock API call
    setTimeout(() => {
      setLoading(false);
      setStep(4);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }, 1000);
  };

  const handleResendOTP = () => {
    if (resendTimer > 0) return;
    
    setResendTimer(60);
    const interval = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    // Mock resend API call
    console.log('Resending OTP to:', email);
  };

  return (
    <Container style={{
      background:
        "radial-gradient(circle at 20% 80%, rgb(123, 108, 246) 0%, transparent 50%), radial-gradient(circle at 41% 20%, rgb(111, 168, 220) 0%, transparent 85%), radial-gradient(circle at 70% 30%, rgb(184, 198, 219) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgb(214, 222, 232) 0%, transparent 45%), #e6edf5",
      backgroundBlendMode: 'normal',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    }}>
      <div style={{
        maxWidth: '550px',
        width: '100%',
        background: 'white',
        borderRadius: '16px',
        padding: '80px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h1 style={{ 
            fontSize: '1.5rem', 
            fontWeight: '600', 
            color: '#1f2937',
            margin: 0
          }}>
            <img src="/images/logo/bmm.png" alt="Logo" style={{ height: '70px' }} />
          </h1>
        </div>

        <BiometricContainer>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            style={{ width: '100%' }}
          >
            {step !== 4 && (
              <BackButton onClick={() => step === 1 ? navigate('/login') : setStep(step - 1)}>
                <FiArrowLeft /> Back
              </BackButton>
            )}
            
            {/* Step 1: Email Input */}
            {step === 1 && (
              <>
                <Title>Forgot Password?</Title>
                <Subtitle>Enter your email address and we'll send you an OTP to reset your password.</Subtitle>
                
                {error && <ErrorMessage>{error}</ErrorMessage>}
                
                <Form onSubmit={handleSendOTP}>
                  <InputGroup>
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      style={{ 
                        background: '#ffffff',
                        border: '1px solid #e3e3e3',
                        padding: '0.75rem 1rem',
                        fontSize: '1.1rem'
                      }}
                    />
                  </InputGroup>
                  
                  <Button 
                    type="submit" 
                    disabled={loading}
                    style={{
                      background: '#00A1FF',
                      width: '100%',
                      padding: '0.875rem',
                      fontSize: '1.1rem',
                      fontWeight: '500',
                      borderRadius: '8px',
                      border: 'none',
                      color: 'white',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.6 : 1
                    }}
                  >
                    {loading ? 'Sending...' : 'Send OTP'}
                  </Button>
                </Form>
              </>
            )}
          
          {/* Step 2: OTP Verification */}
          {step === 2 && (
            <>
              <Title>Enter OTP</Title>
              <Subtitle>We've sent a 6-digit code to {email}</Subtitle>
              
              {error && <ErrorMessage>{error}</ErrorMessage>}
              
              <Form onSubmit={handleVerifyOTP}>
                <OTPContainer>
                  {otp.map((digit, index) => (
                    <OTPInput
                      key={index}
                      id={`otp-${index}`}
                      type="text"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleOTPChange(index, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace' && !digit && index > 0) {
                          document.getElementById(`otp-${index - 1}`).focus();
                        }
                      }}
                    />
                  ))}
                </OTPContainer>
                
                <Button 
                  type="submit" 
                  disabled={loading}
                  style={{
                    background: '#00A1FF',
                    width: '100%',
                    padding: '0.875rem',
                    fontSize: '1.1rem',
                    fontWeight: '500',
                    borderRadius: '8px',
                    border: 'none',
                    color: 'white',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </Button>
                
                <div style={{ textAlign: 'center' }}>
                  <ResendLink 
                    onClick={handleResendOTP} 
                    disabled={resendTimer > 0}
                  >
                    {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
                  </ResendLink>
                </div>
              </Form>
            </>
          )}
          
          {/* Step 3: Reset Password */}
          {step === 3 && (
            <>
              <Title>Reset Password</Title>
              <Subtitle>Create a new password for your account</Subtitle>
              
              {error && <ErrorMessage>{error}</ErrorMessage>}
              
              <Form onSubmit={handleResetPassword}>
                <InputGroup>
                  <Label>New Password</Label>
                  <Input
                    type="password"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    style={{ 
                      background: '#ffffff',
                      border: '1px solid #e3e3e3',
                      padding: '0.75rem 1rem',
                      fontSize: '1.1rem'
                    }}
                  />
                  <PasswordStrengthBar>
                    <PasswordStrengthFill strength={passwordStrength()} />
                  </PasswordStrengthBar>
                </InputGroup>
                
                <InputGroup>
                  <Label>Confirm Password</Label>
                  <Input
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    style={{ 
                      background: '#ffffff',
                      border: '1px solid #e3e3e3',
                      padding: '0.75rem 1rem',
                      fontSize: '1.1rem'
                    }}
                  />
                </InputGroup>
                
                <PasswordRequirements>
                  <Requirement met={passwordRequirements.minLength}>
                    {passwordRequirements.minLength && <CheckIcon />}
                    At least 8 characters
                  </Requirement>
                  <Requirement met={passwordRequirements.hasUpperCase}>
                    {passwordRequirements.hasUpperCase && <CheckIcon />}
                    One uppercase letter
                  </Requirement>
                  <Requirement met={passwordRequirements.hasNumber}>
                    {passwordRequirements.hasNumber && <CheckIcon />}
                    One number
                  </Requirement>
                  <Requirement met={passwordRequirements.hasSpecialChar}>
                    {passwordRequirements.hasSpecialChar && <CheckIcon />}
                    One special character
                  </Requirement>
                </PasswordRequirements>
                
                <div style={{ marginTop: '1.5rem' }}>
                  <Button 
                    type="submit" 
                    disabled={loading || !allRequirementsMet}
                    style={{
                      background: '#00A1FF',
                      width: '100%',
                      padding: '0.875rem',
                      fontSize: '1.1rem',
                      fontWeight: '500',
                      borderRadius: '8px',
                      border: 'none',
                      color: 'white',
                      cursor: (loading || !allRequirementsMet) ? 'not-allowed' : 'pointer',
                      opacity: (loading || !allRequirementsMet) ? 0.6 : 1
                    }}
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </div>
              </Form>
            </>
          )}
          
          {/* Step 4: Success */}
          {step === 4 && (
            <SuccessMessage>
              <FiCheck style={{ width: '64px', height: '64px' }} />
              <h2>Password Reset Successful!</h2>
              <p>Your password has been successfully reset. You will be redirected to the login page.</p>
              <Button 
                onClick={() => navigate('/login')}
                style={{
                  background: '#00A1FF',
                  width: '100%',
                  padding: '0.875rem',
                  fontSize: '1.1rem',
                  fontWeight: '500',
                  borderRadius: '8px',
                  border: 'none',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Go to Login
              </Button>
            </SuccessMessage>
          )}
          </motion.div>
        </BiometricContainer>
      </div>
    </Container>
  );
};

export default ForgotPassword;
